import { Coins, Users, Crown, TrendingUp, PiggyBank } from "lucide-react";

interface ContestData {
  id: number;
  title: string;
  timer: number | null;
  maxbet: number;
  status: number;
  createdAt: Date;
  options: ContestOptionData[];
  bets: BetsData[] | null;
  winnerOptionId: number | null;
}

interface ContestOptionData {
  id: number;
  contestId: number;
  command: string;
  description: string;
  totalBets: number;
  biggestBetName: string | null;
}

interface BetsData {
  optionId: number;
  twitch_id: number;
  username: string;
  amount: number;
  timestamp: string;
}

export function ContestDisplay({ data }: Readonly<{ data: ContestData }>) {
  if (!data?.bets) {
    return null;
  }

  const totalUsers =
    data.bets?.length > 0
      ? new Set(data.bets.map((bet) => bet.username)).size
      : 0;
  const totalBets = data.bets?.reduce((sum, bet) => sum + bet.amount, 0) ?? 0;

  const optionTotals = data.options.map((opt) => {
    const optionBets =
      data.bets?.filter((bet) => bet.optionId === opt.id) ?? [];
    const totalAmount = optionBets.reduce((sum, bet) => sum + bet.amount, 0);

    return {
      ...opt,
      totalAmount,
      percentage:
        totalBets > 0 ? ((totalAmount / totalBets) * 100).toFixed(1) : "0",
      isWinner: data.winnerOptionId === opt.id,
    };
  });

  // Status: 0 = open, 1 = locked, 2 = ended
  const statusColors = {
    0: "bg-green-500/20 border-green-500/50",
    1: "bg-yellow-500/20 border-yellow-500/50",
    2: "bg-red-500/20 border-red-500/50",
  };

  return (
    <div className="nyeremenyjatek-container rounded-3xl ">
      <div
        className={`relative animate-[scale_700ms_ease-in-out] w-full h-full bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 p-6 shadow-2xl backdrop-blur-sm flex flex-col justify-evenly border-2 rounded-3xl ${
          statusColors[data.status as keyof typeof statusColors]
        }`}
      >
        {/* Header */}
        <div className="flex flex-col items-center mb-1">
          <h2 className="text-xl font-bold text-white">{data.title}</h2>
          <div className="text-sm flex items-center flex-row py-3">
            {data.status === 0 && (
              <span className="bg-slate-500 rounded-lg py-1 px-2 uppercase mr-1">
                Type in chat: <b>!bet option amount</b>
              </span>
            )}
            {data.status === 1 && (
              <span className="bg-yellow-500 rounded-lg py-1 px-2 uppercase mr-1">
                Bets closed!
              </span>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          <div className="bg-zinc-800/40 backdrop-blur-sm rounded-xl p-3 pb-6 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <Users className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">
                  Users
                </p>
                <p className="text-xl font-bold text-white">{totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/40 backdrop-blur-sm rounded-xl p-3 pb-6 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-green-500/10 rounded-lg">
                <Coins className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">
                  Total
                </p>
                <p className="text-xl font-bold text-white">{totalBets}</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/40 backdrop-blur-sm rounded-xl p-3 pb-6 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-green-500/10 rounded-lg">
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">
                  Maxbet
                </p>
                <p className="text-xl font-bold text-white">{data.maxbet}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div
          className={`gap-2 ${
            optionTotals.length > 2 ? "grid grid-cols-2" : "flex flex-col"
          }`}
        >
          {optionTotals.map((option, index) => (
            <div
              key={option.id}
              className={`backdrop-blur-sm h-full rounded-xl p-2 px-4 flex flex-col m-auto gap-2 justify-center items-center w-full border transition-all ${
                option.isWinner
                  ? "bg-green-800/40 border-green-500/70 shadow-lg shadow-green-500/20"
                  : "bg-zinc-800/40 border-zinc-700/50 hover:border-zinc-600/50"
              }`}
            >
              <div className="flex items-center justify-between w-full py-2">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${
                      option.isWinner
                        ? "bg-gradient-to-br from-green-500 to-emerald-600"
                        : "bg-gradient-to-br from-orange-500 to-yellow-300"
                    }`}
                  >
                    {option.isWinner ? (
                      <Crown className="w-5 h-5" />
                    ) : (
                      String.fromCharCode(65 + index)
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {option.description}
                    </p>
                    <p className="text-xs text-zinc-400">
                      {option.command} amount
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-white">
                    {option.totalAmount}
                  </p>
                  <p className="text-xs text-zinc-400">{option.percentage}%</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-zinc-700/30 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    option.isWinner
                      ? "bg-gradient-to-r from-green-500 to-emerald-500"
                      : "bg-gradient-to-r from-purple-500 to-pink-500"
                  }`}
                  style={{ width: `${option.percentage}%` }}
                />
              </div>

              {/* Biggest Bet */}
              {option.biggestBetName && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <PiggyBank className="w-4 h-4 text-yellow-400" />
                  <span className="py-1">{option.biggestBetName}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export async function getActiveContest() {
  const response = await fetch(
    "https://api.palinagyonbuzi.com/api/contest/active",
    {
      headers: {
        Authorization: "Bearer f2414060-927a-411b-a481-197241e5828d",
      },
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch active contest");
  }
  const data = await response.json();

  console.log("Active Contest Data:", data);
  return data;
}

export const handleContestBet = (prevContest: any, newBet: any) => {
  if (!prevContest) return prevContest;

  const betExists = prevContest.bets?.some(
    (bet: any) =>
      bet.twitch_id === newBet.twitch_id &&
      bet.optionId === newBet.optionId &&
      bet.timestamp === newBet.timestamp,
  );

  if (betExists) return prevContest;

  const updatedBets = [...(prevContest.bets || []), newBet];

  const updatedOptions = prevContest.options.map((opt: any) => {
    if (opt.id === newBet.optionId) {
      const optionBets = updatedBets.filter(
        (bet: any) => bet.optionId === opt.id,
      );
      const biggestBet = optionBets.reduce(
        (max, bet) => (bet.amount > max.amount ? bet : max),
        { amount: 0, username: null },
      );

      return {
        ...opt,
        totalBets: opt.totalBets + 1,
        biggestBetName: biggestBet.username,
      };
    }
    return opt;
  });

  return {
    ...prevContest,
    bets: updatedBets,
    options: updatedOptions,
  };
};

export const handleContestStop = (prevContest: any, winnerOptionId: number) => {
  if (!prevContest) return prevContest;

  return {
    ...prevContest,
    status: 1, // ended
    winnerOptionId,
  };
};

export const handleContestWinner = (prevContest: any, winnerData: any) => {
  if (!prevContest) return prevContest;

  return {
    ...prevContest,
    status: 1,
    winnerOptionId: winnerData.winnerOptionId,
  };
};
